import { Link } from "react-router-dom";
import { useLang, formatRelativeTime, categoryName } from "@/i18n";

const FALLBACK = "https://images.unsplash.com/photo-1495020689067-958852a7765e?w=900";

export function NewsCard({ item, size = "md" }) {
  const { lang } = useLang();
  if (!item) return null;
  const imgClass =
    size === "lg"
      ? "aspect-[16/10]"
      : size === "sm"
      ? "aspect-[16/10]"
      : "aspect-[4/3]";
  const titleClass =
    size === "lg"
      ? "text-2xl sm:text-3xl lg:text-4xl"
      : size === "sm"
      ? "text-base"
      : "text-lg sm:text-xl";

  return (
    <Link
      to={`/news/${item.id}`}
      className="news-card block group"
      data-testid={`news-card-${item.id}`}
    >
      <div className={`relative overflow-hidden bg-neutral-900 ${imgClass}`}>
        <img
          src={item.image || FALLBACK}
          alt={item.title}
          loading="lazy"
          onError={(e) => { e.target.src = FALLBACK; }}
          className="card-image w-full h-full object-cover"
        />
        <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
          {categoryName(item.category, lang)}
        </span>
      </div>
      <div className="pt-4">
        <h3 className={`card-title headline ${titleClass} text-white transition-colors`}>
          {item.title}
        </h3>
        {size !== "sm" && item.description && (
          <p className="mt-2 text-sm text-neutral-400 line-clamp-2 leading-relaxed">
            {item.description}
          </p>
        )}
        <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500 font-mono uppercase tracking-wider">
          <span>{item.source}</span>
          <span>•</span>
          <span>{formatRelativeTime(item.published_at, lang)}</span>
        </div>
      </div>
    </Link>
  );
}

export function HeroCard({ item }) {
  const { lang } = useLang();
  if (!item) return null;
  return (
    <Link to={`/news/${item.id}`} className="relative block group overflow-hidden h-[420px] sm:h-[520px] news-card" data-testid={`hero-card-${item.id}`}>
      <img
        src={item.image || FALLBACK}
        alt={item.title}
        onError={(e) => { e.target.src = FALLBACK; }}
        className="card-image absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="bg-red-600 text-white text-[11px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm">
            {categoryName(item.category, lang)}
          </span>
          <span className="text-white/80 text-xs font-mono uppercase tracking-wider">
            {item.source} · {formatRelativeTime(item.published_at, lang)}
          </span>
        </div>
        <h2 className="headline text-3xl sm:text-5xl lg:text-6xl text-white max-w-4xl text-balance">
          {item.title}
        </h2>
        <p className="mt-4 text-base sm:text-lg text-white/90 max-w-2xl line-clamp-2 leading-relaxed">
          {item.description}
        </p>
      </div>
    </Link>
  );
}

export function ListCard({ item }) {
  const { lang } = useLang();
  if (!item) return null;
  return (
    <Link to={`/news/${item.id}`} className="news-card group flex gap-3 py-3 border-b border-neutral-800 last:border-0" data-testid={`list-card-${item.id}`}>
      <div className="relative w-24 sm:w-28 h-20 sm:h-24 flex-shrink-0 overflow-hidden bg-neutral-900">
        <img
          src={item.image || FALLBACK}
          alt={item.title}
          loading="lazy"
          onError={(e) => { e.target.src = FALLBACK; }}
          className="card-image w-full h-full object-cover"
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="eyebrow text-red-500">{categoryName(item.category, lang)}</span>
        <h4 className="card-title headline text-sm sm:text-base mt-1 line-clamp-2 text-white transition-colors">
          {item.title}
        </h4>
        <p className="mt-1 text-xs text-neutral-500 font-mono">
          {item.source} · {formatRelativeTime(item.published_at, lang)}
        </p>
      </div>
    </Link>
  );
}
